import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { AmountInput } from "../components/ui/amount-input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { formatINR, formatDate, todayISO } from "../lib/format";
import { Plus, TrendingUp, Search } from "lucide-react";
import { toast } from "sonner";
import { BackdateFields } from "../components/BackdateFields";
import { useDuplicateGuard } from "../components/DuplicateGuard";

export default function Investors() {
    const { guard, dialog: dupDialog } = useDuplicateGuard();
    const [list, setList] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [open, setOpen] = useState(false);
    const [custQuery, setCustQuery] = useState("");
    const [form, setForm] = useState({
        customer_id: "",
        principal: 100000,
        interest_rate: 12,
        period_months: 12,
        start_date: todayISO(),
        notes: "",
    });

    async function load() {
        const { data } = await api.get("/investors");
        setList(data);
    }
    useEffect(() => {
        load();
        api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
    }, []);

    const selectedCustomer = useMemo(
        () => customers.find((c) => c.id === form.customer_id),
        [customers, form.customer_id]
    );

    const filteredCustomers = useMemo(() => {
        const q = custQuery.trim().toLowerCase();
        if (!q) return customers.slice(0, 200);
        return customers.filter((c) => {
            const hay = `${c.name || ""} ${c.customer_code || ""} ${c.mobile || ""}`.toLowerCase();
            return hay.includes(q);
        }).slice(0, 200);
    }, [customers, custQuery]);

    async function create() {
        if (!form.customer_id) return toast.error("Select an existing customer");
        await guard({ collection_type: "investment", customer_id: form.customer_id, amount: Number(form.principal), on_date: form.start_date }, doCreate);
    }

    async function doCreate() {
        try {
            await api.post("/investors", form);
            toast.success("Investor added under selected customer");
            setOpen(false);
            setForm({
                customer_id: "",
                principal: 100000,
                interest_rate: 12,
                period_months: 12,
                start_date: todayISO(),
                notes: "",
            });
            setCustQuery("");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Investors</h1>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="new-investor-btn" className="h-11 rounded-xl bg-blue-900 hover:bg-blue-800 tap">
                            <Plus className="h-4 w-4 mr-1.5" /> New Investor
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-2xl max-w-lg">
                        <DialogHeader><DialogTitle>Add Investor</DialogTitle></DialogHeader>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div className="sm:col-span-2">
                                <Label>Customer</Label>
                                <div className="relative mb-2">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                    <Input
                                        data-testid="inv-customer-search"
                                        placeholder="Search by name, code or mobile…"
                                        value={custQuery}
                                        onChange={(e) => setCustQuery(e.target.value)}
                                        className="h-11 rounded-xl pl-9"
                                    />
                                </div>
                                <Select
                                    value={form.customer_id}
                                    onValueChange={(v) => setForm({ ...form, customer_id: v })}
                                >
                                    <SelectTrigger className="h-11 rounded-xl" data-testid="inv-customer-select">
                                        <SelectValue placeholder="Select existing customer" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-72">
                                        {filteredCustomers.length === 0 ? (
                                            <div className="px-3 py-6 text-center text-xs text-slate-400">
                                                No customers match “{custQuery}”
                                            </div>
                                        ) : filteredCustomers.map((c) => (
                                            <SelectItem
                                                key={c.id}
                                                value={c.id}
                                                data-testid={`inv-customer-opt-${c.id}`}
                                            >
                                                {c.name} · {c.customer_code}
                                                {c.mobile ? ` · ${c.mobile}` : ""}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {selectedCustomer && (
                                    <div
                                        className="mt-2 rounded-xl border border-blue-100 bg-blue-50/60 p-2.5 text-xs text-slate-700"
                                        data-testid="inv-customer-preview"
                                    >
                                        <div className="font-semibold text-slate-900">
                                            {selectedCustomer.name}
                                            <span className="ml-2 text-[10px] font-mono uppercase bg-white text-slate-600 px-1.5 py-0.5 rounded border border-slate-200">
                                                {selectedCustomer.customer_code}
                                            </span>
                                        </div>
                                        <div className="mt-0.5 text-slate-600">
                                            {selectedCustomer.mobile || "—"}
                                            {selectedCustomer.address ? ` · ${selectedCustomer.address}` : ""}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div>
                                <Label>Principal (₹)</Label>
                                <AmountInput data-testid="inv-principal" value={form.principal}
                                    onChange={(v) => setForm({ ...form, principal: v })}
                                    className="h-11 rounded-xl" />
                            </div>
                            <div>
                                <Label>Interest Rate (% per annum)</Label>
                                <AmountInput data-testid="inv-rate" value={form.interest_rate}
                                    onChange={(v) => setForm({ ...form, interest_rate: v })}
                                    className="h-11 rounded-xl" />
                            </div>
                            <div>
                                <Label>Period (months)</Label>
                                <AmountInput data-testid="inv-period" value={form.period_months}
                                    onChange={(v) => setForm({ ...form, period_months: v })}
                                    className="h-11 rounded-xl" />
                            </div>
                            <div>
                                <Label>Start Date</Label>
                                <Input data-testid="inv-start" type="date" value={form.start_date}
                                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                                    className="h-11 rounded-xl" />
                            </div>
                            <div className="sm:col-span-2">
                                <BackdateFields timeOnly value={form} onChange={setForm} />
                            </div>
                            <div className="sm:col-span-2">
                                <Label>Notes</Label>
                                <Textarea rows={2} value={form.notes}
                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    className="rounded-xl" />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button onClick={create} data-testid="save-investor-btn"
                                className="bg-blue-900 hover:bg-blue-800">Save</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {list.length === 0 ? (
                <Card className="rounded-2xl border-slate-100 p-8 text-center text-sm text-slate-400">
                    <TrendingUp className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                    No investors yet
                </Card>
            ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                    {list.map((i) => (
                        <Link key={i.id} to={`/investors/${i.id}`} data-testid={`investor-row-${i.id}`}>
                            <Card className="rounded-2xl border-slate-100 p-4 card-hover">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <div className="font-semibold text-slate-900">{i.name}</div>
                                            <span className="text-[10px] font-mono uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                                {i.investor_code}
                                            </span>
                                        </div>
                                        <div className="text-xs text-slate-500 mt-0.5">
                                            {i.mobile} · {i.interest_rate}% p.a. · {i.period_months} mo
                                        </div>
                                    </div>
                                    <Badge className={i.status === "active"
                                        ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50"
                                        : "bg-slate-100 text-slate-700"}>
                                        {i.status}
                                    </Badge>
                                </div>
                                <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-2">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Principal</div>
                                        <div className="num font-bold text-slate-900">{formatINR(i.principal)}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Matures</div>
                                        <div className="text-sm font-semibold text-slate-700">{formatDate(i.maturity_date)}</div>
                                    </div>
                                </div>
                            </Card>
                        </Link>
                    ))}
                </div>
            )}
            {dupDialog}
        </div>
    );
}
